/**
 * Merges subarrays of a two-dimensional array into an ordered one-dimensional array,
 * applying deduplication based on a key retrieved by the keyItem function.
 *
 * The merging order is column-wise: A1, B1, C1, A2, B2, C2, etc., where A, B, C
 * are subarrays and A1 is the 1st element of array A.
 *
 * Deduplication: If the key of an element is already present in the result set,
 * the function skips this element and checks the next element in the same subarray
 * until a distinct key is found or the subarray ends.
 *
 * @template T The type of elements in the subarrays.
 * @template K The type of the key used for deduplication.
 * @param {Array<Array<T>>} list - The list of subarrays to merge.
 * @param {function(T): K} keyItem - A function that returns the key for an element, used for deduplication.
 * @returns {Array<T>} The merged and deduplicated list of elements.
 */
export function mergeSort<T, K>(list: Array<Array<T>>, keyItem: (item: T) => K): Array<T> {
  // Use a Set to keep track of keys already added to the result for deduplication.
  const seenKeys = new Set<K>();
  const result: Array<T> = [];

  // Find the maximum length among all subarrays to determine the number of columns to process.
  const maxLength = Math.max(0, ...list.map((sublist) => sublist.length));

  // Iterate through each "column" (index i)
  for (let i = 0; i < maxLength; i++) {
    // Iterate through each subarray (list[j])
    for (let j = 0; j < list.length; j++) {
      const sublist = list[j];
      let currentElementIndex = i; // Start checking from the current column index

      // While loop to find the next element in the current sublist
      // whose key has not been seen yet.
      while (currentElementIndex < sublist.length) {
        const currentElement = sublist[currentElementIndex];
        const key = keyItem(currentElement);

        // If the key has not been seen, add the element to the result
        // and the key to the seenKeys set.
        if (!seenKeys.has(key)) {
          result.push(currentElement);
          seenKeys.add(key);
          break; // Found a valid element for this column in this sublist, move to the next sublist
        }

        // If the key has been seen, move to the next element in the same sublist.
        currentElementIndex++;
      }
      // If the while loop finishes without finding a valid element
      // (i.e., currentElementIndex >= sublist.length), it means all
      // remaining elements in this sublist from index i onwards have
      // keys that are already in the result. The loop for this sublist
      // for the current column (i) effectively ends.
    }
  }

  return result;
}
